import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAllArticlePublished } from "../../../services/article.service";
import { getAllRubriques } from "../../../services/rubrique.service";
import rubriqueHero from "../../../assets/herobg/rubrique-hero.webp";
import { MainCard } from "../index";
import presentationBg from '../../../assets/bg/presentation.webp';
import { useDateFormatter } from "../../../hooks";

// URL de base pour les images - une seule URL de base
const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

const PresentationRubrique = () => {
    const { id } = useParams(); // Obtenir l'ID de la rubrique à partir des paramètres d'URL
    const [rubriqueData, setRubriqueData] = useState(null);
    const [filteredCards, setFilteredCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const navigate = useNavigate();
    const formatDate = useDateFormatter();

    // Fonction pour formater les rubriques en chaîne de caractères
    const formatRubriques = (rubriques) => {
        if (!rubriques || !Array.isArray(rubriques) || rubriques.length === 0) {
            return '';
        }

        return rubriques.map(rubrique => rubrique.name_rub).join(', ');
    };

    // Fonction pour obtenir le chemin d'image complet
    const getImagePath = (photoPath) => {
        if (!photoPath) return '';

        // Si le chemin commence déjà par http, c'est une URL complète
        if (photoPath.startsWith('http')) return photoPath;

        // S'assurer que la baseURL n'a pas de slash à la fin
        const cleanBaseUrl = REACT_APP_BASE_URL.endsWith('/')
            ? REACT_APP_BASE_URL.slice(0, -1)
            : REACT_APP_BASE_URL;

        // Le chemin de l'API inclut déjà "storage/" dans la réponse
        return `${cleanBaseUrl}/${photoPath}`;
    };

    useEffect(() => {
        const fetchRubriqueAndArticles = async () => {
            try {
                setLoading(true);
                setError(false);

                // Appel de l'API pour récupérer les rubriques
                const rubriquesResponse = await getAllRubriques();
                const rubriques = rubriquesResponse.data;

                // Trouver la rubrique correspondant à l'ID
                const rubriqueName = id.replace(/_/g, ' ').toLowerCase();
                const foundRubrique = rubriques.find(
                    (rubrique) => rubrique.name_rub.toLowerCase() === rubriqueName
                );

                if (foundRubrique) {
                    setRubriqueData(foundRubrique);

                    // Appel de l'API pour récupérer les articles
                    const articlesResponse = await getAllArticlePublished(1, 100); // Limite de 100 articles
                    const articles = articlesResponse.data;

                    // Filtrer les articles pour la rubrique sélectionnée
                    const cardsForThisRubrique = articles.filter(article => {
                        // Si l'article a un tableau de rubriques
                        if (article.rubriques && Array.isArray(article.rubriques) && article.rubriques.length > 0) {
                            // Vérifier si l'une des rubriques correspond à l'ID de la rubrique sélectionnée
                            return article.rubriques.some(rub => rub.id_rub === foundRubrique.id_rub);
                        }
                        // Sinon, vérifier l'ancienne structure avec id_rub
                        else if (article.id_rub) {
                            return article.id_rub === foundRubrique.id_rub;
                        }
                        return false;
                    });

                    setFilteredCards(cardsForThisRubrique);
                } else {
                    setRubriqueData(null);
                    setFilteredCards([]);
                }
            } catch (err) {
                console.error('Erreur lors de la récupération des données :', err);
                setError(true);
                setRubriqueData(null);
                setFilteredCards([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRubriqueAndArticles();
    }, [id]);

    // Fonction pour obtenir la rubrique à afficher d'un article
    const getRubriqueDisplay = (article) => {
        // Si l'article a un tableau de rubriques, l'utiliser
        if (article.rubriques && Array.isArray(article.rubriques) && article.rubriques.length > 0) {
            return formatRubriques(article.rubriques);
        }
        // Sinon, essayer d'utiliser name_rub
        else if (article.name_rub) {
            return article.name_rub;
        }
        // Fallback au cas où aucune rubrique n'est trouvée
        return "Non catégorisé";
    };

    // Fonction pour gérer le clic sur un article
    const handleArticleClick = (slug) => {
        navigate(`/article/${slug}`);
    };

    return (
        <div>
            {/* Section avec arrière-plan */}
            <div className="mb-4 sm:mb-6 md:mb-8 relative overflow-hidden">
                {/* Image d'arrière-plan avec opacité réduite */}
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-50"
                    style={{
                        backgroundImage: `url(${presentationBg})`,
                        zIndex: -1
                    }}
                ></div>

                {/* Contenu positionné sur l'image d'arrière-plan */}
                <div className="relative max-w-7xl mx-auto z-10 p-4 sm:p-6 md:p-8">
                    {rubriqueData ? (
                        <>
                            <div className="flex flex-col md:flex-row items-center md:space-x-28 mb-6 sm:mb-8 md:mb-12">
                                {/* Afficher l'icône de la rubrique */}
                                {rubriqueData.photo_rub && (
                                    <div className="mb-6 md:mb-0 flex justify-center md:justify-start w-full md:w-auto">
                                        <img
                                            src={getImagePath(rubriqueData.photo_rub)}
                                            alt={rubriqueData.name_rub}
                                            className="w-40 h-40 object-cover rounded-md" // Dimensions uniformisées
                                            onError={(e) => {
                                                e.target.src = '/default-icon.png';
                                            }}
                                        />
                                    </div>
                                )}
                                <div className="text-justify">
                                    <h2 className="text-2xl sm:text-3xl md:text-4xl text-greenprimary font-semibold flex items-center mb-3 sm:mb-4 md:mb-5">
                                        <span className="w-8 sm:w-10 md:w-16 h-1 sm:h-1.5 md:h-2.5 mt-1.5 sm:mt-2 md:mt-3 bg-greenprimary mr-2"></span>
                                        {rubriqueData.name_rub}
                                    </h2>
                                    <p className="font-light text-sm sm:text-base">
                                        {rubriqueData.description_rub || "Aucune description disponible pour cette rubrique."}
                                    </p>
                                </div>
                            </div>
                        </>
                    ) : loading ? (
                        <div className="text-center py-12">
                            <p className="text-lg text-gray-600">Chargement des informations de la rubrique...</p>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-lg text-red-500">
                                Rubrique non trouvée.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Contenu de la rubrique - Articles filtrés */}
            <div className="flex-grow px-6 sm:px-10 lg:px-16 max-w-7xl mx-auto w-full mb-12 mt-12">
                {loading ? (
                    <div className="text-center py-12">
                        <p className="text-lg text-gray-600">Chargement des articles...</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-12">
                        <p className="text-lg text-red-500">
                            Une erreur s'est produite lors du chargement des articles.
                        </p>
                    </div>
                ) : filteredCards.length > 0 ? (
                    <section className="mb-12">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredCards.map((card, cardIndex) => (
                                <div
                                    key={card.id_art}
                                    className="cursor-pointer"
                                    onClick={() => handleArticleClick(card.slug)}
                                >
                                    <MainCard
                                        key={cardIndex}
                                        id={card.id_art}
                                        cover={card.photo_art ? getImagePath(card.photo_art) : rubriqueHero}
                                        rubrique={getRubriqueDisplay(card)}
                                        title={card.title_art.fr}
                                        description={card.key_words_art.fr.join(", ")}
                                        date={formatDate(card.date_pub_art)}
                                        auteur={`${card.author.title_aut} ${card.author.first_name} ${card.author.last_name}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </section>
                ) : (
                    <div className="text-center py-12">
                        <p className="text-lg text-gray-600">
                            Aucun article disponible dans cette rubrique pour le moment.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PresentationRubrique;